import React, { useEffect, useRef, useState } from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import { AudienceSelection, SavedAudience } from '@marketing-milk/interfaces'
import { useFetchAllAudiences } from 'hooks/audiences/useFetchAllAudiences'
import { PacketModalShell } from '../../components/PacketModalShell'
import { radius, SurfacePalette, type } from '../../tokens'
import { audienceSizeText } from './audienceTargeting'
import { audienceTypes } from '@marketing-milk/frontend'

interface Props {
  palette: SurfacePalette
  businessID: number
  current: AudienceSelection | null
  saving?: boolean
  onSave: (selection: AudienceSelection) => void
  onClose: () => void
}

/**
 * Edit-only picker for the packet's audience selection. Lists the business's
 * saved audiences (the source of truth — audiences don't reliably attach to a
 * sprint at packet time), lets the AE choose which to feature and mark one
 * primary, and persists via the packet update path. When the packet has no
 * selection yet, the business's default audiences are pre-checked as an
 * UNSAVED suggestion — nothing is written until the AE saves.
 */
export const AudienceSelectionEditor = ({
  palette,
  businessID,
  current,
  saving,
  onSave,
  onClose,
}: Props) => {
  const { audiences, isLoading, defaultAudiences } = useFetchAllAudiences(String(businessID))

  const [selectedIDs, setSelectedIDs] = useState<number[]>(current?.audienceIDs ?? [])
  const [primaryID, setPrimaryID] = useState<number | null>(current?.primaryAudienceID ?? null)

  // First-time suggestion: when the packet has never had a selection saved,
  // pre-check the business's default audiences once they load. This is an
  // UNSAVED suggestion — it only updates local state, never writes. Skipped if
  // the AE has already touched the form or any selection (even an empty one)
  // has been saved before — a deliberate "clear all" must stick.
  const touched = useRef(false)
  useEffect(() => {
    if (touched.current) return
    if (current !== null) return
    if (defaultAudiences.length === 0) return
    const ids = defaultAudiences.map(a => a.id)
    setSelectedIDs(ids)
    setPrimaryID(ids[0] ?? null)
  }, [current, defaultAudiences])

  const toggle = (id: number) => {
    touched.current = true
    setSelectedIDs(prev => {
      if (prev.includes(id)) {
        const next = prev.filter(x => x !== id)
        // Keep primary valid: fall back to the first remaining selection.
        setPrimaryID(p => (p === id ? next[0] ?? null : p))
        return next
      }
      const next = [...prev, id]
      setPrimaryID(p => p ?? id)
      return next
    })
  }

  const save = () => {
    const primaryAudienceID =
      primaryID !== null && selectedIDs.includes(primaryID) ? primaryID : selectedIDs[0] ?? null
    onSave({ audienceIDs: selectedIDs, primaryAudienceID })
  }

  return (
    <PacketModalShell
      palette={palette}
      title="Select audiences"
      subtitle="Choose which saved audiences appear in this packet and mark one as primary."
      onClose={onClose}
      maxWidth={560}
    >
      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: palette.textMuted }}>
          <CircularProgress size={18} style={{ color: palette.accent }} />
          <span style={{ fontSize: type.size.body }}>Loading audiences…</span>
        </div>
      ) : !audiences || audiences.length === 0 ? (
        <p style={{ margin: 0, fontSize: type.size.body, color: palette.textMuted }}>
          This business has no saved audiences yet.
        </p>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxHeight: '56vh',
            overflowY: 'auto',
          }}
        >
          {audiences.map(a => (
            <AudienceRow
              key={a.id}
              palette={palette}
              audience={a}
              selected={selectedIDs.includes(a.id)}
              isPrimary={primaryID === a.id}
              onToggle={() => toggle(a.id)}
              onMakePrimary={() => {
                touched.current = true
                setPrimaryID(a.id)
              }}
            />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button type="button" onClick={onClose} style={ghostBtn(palette)}>
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          style={primaryBtn(palette, !!saving)}
        >
          {saving ? 'Saving…' : 'Save selection'}
        </button>
      </div>
    </PacketModalShell>
  )
}

const AudienceRow = ({
  palette,
  audience,
  selected,
  isPrimary,
  onToggle,
  onMakePrimary,
}: {
  palette: SurfacePalette
  audience: SavedAudience
  selected: boolean
  isPrimary: boolean
  onToggle: () => void
  onMakePrimary: () => void
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '10px 12px',
      borderRadius: radius.sm,
      border: `1px solid ${selected ? palette.accent : palette.hairline}`,
      background: palette.surface,
    }}
  >
    <input
      type="checkbox"
      checked={selected}
      onChange={onToggle}
      aria-label={`Select ${audience.name}`}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontFamily: type.family.sans,
          fontSize: type.size.body,
          fontWeight: type.weight.medium,
          color: palette.text,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={audience.name}
      >
        {audience.name}
      </div>
      <div style={{ fontSize: type.size.eyebrow, color: palette.textMuted }}>
        {(audienceTypes[audience.type]?.title ?? audience.type) +
          ' · ' +
          audienceSizeText(audience)}
      </div>
    </div>
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: type.size.eyebrow,
        color: selected ? palette.text : palette.textFaint,
        cursor: selected ? 'pointer' : 'default',
      }}
    >
      <input
        type="radio"
        name="primary-audience"
        checked={isPrimary}
        disabled={!selected}
        onChange={onMakePrimary}
        aria-label={`Make ${audience.name} primary`}
      />
      Primary
    </label>
  </div>
)

const ghostBtn = (palette: SurfacePalette): React.CSSProperties => ({
  border: `1px solid ${palette.hairline}`,
  background: 'transparent',
  color: palette.textMuted,
  borderRadius: radius.sm,
  padding: '8px 16px',
  fontFamily: type.family.sans,
  fontSize: type.size.body,
  cursor: 'pointer',
})

const primaryBtn = (palette: SurfacePalette, disabled: boolean): React.CSSProperties => ({
  border: `1px solid ${palette.accent}`,
  background: palette.accent,
  color: palette.accentInk,
  borderRadius: radius.sm,
  padding: '8px 16px',
  fontFamily: type.family.sans,
  fontSize: type.size.body,
  fontWeight: type.weight.medium,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1,
})
